import type { APIRoutes } from '@/lib/types'
import { makeApiRoute } from '@/lib/utils/useAPI'

/**
 * API route catalog. Single source of truth for every endpoint
 * the frontend touches. Adding a new endpoint = one line here +
 * one line on the `APIRoutes` interface in
 * `lib/types/API/APIRoutes.ts`.
 *
 * URI template syntax:
 *   - bare resource path → `.get('5')` appends as path segment
 *   - `{?}` markers → consumed in order by varargs to `.get(...)`
 *
 * Server-side route map: see `LockerServer/backend/routes/api.php`.
 */
const ApiRoutes: APIRoutes = {
  // ── Auth (Fortify, served under /api/auth/*) ───────────────────
  // Note: Sanctum's CSRF endpoint is at /sanctum/csrf-cookie (root,
  // NOT under /api/) — call `useAPI().ensureCsrfCookie()` for it,
  // not a `routes.*` entry.
  LOGIN: makeApiRoute('auth/login'),
  LOGOUT: makeApiRoute('auth/logout'),
  AUTH_ME: makeApiRoute('auth/me'),
  // USER impersonation (platform super_admin only): become a tenant admin to
  // see exactly what they see, then revert. Read state / start / stop.
  // NOT the operating-tenant header, which keeps your own identity - see
  // `Lib\Auth\ImpersonationSession` (BE).
  IMPERSONATION: makeApiRoute('auth/impersonation'),
  // Self-service password change — `PATCH /api/auth/me/password`
  // with `password / password_confirmation` body. Does NOT require
  // current_password (trusts the active session). For the
  // current-password-required flow, use `AUTH_USER_PASSWORD`.
  AUTH_ME_PASSWORD: makeApiRoute('auth/me/password'),
  // Self-service email-notification opt-out — `GET/PUT
  // /api/auth/me/notification-preferences`. GET returns the kind
  // catalog with each `enabled` state; PUT persists `{kind, enabled}`
  // toggles. Backs Settings → Profile → Email notifications.
  AUTH_ME_NOTIFICATION_PREFERENCES: makeApiRoute('auth/me/notification-preferences'),
  // Fortify-published — `PUT /api/auth/user/password` with
  // `current_password / password / password_confirmation` body.
  // Kept available for sudo-style flows that want the current-
  // password challenge.
  AUTH_USER_PASSWORD: makeApiRoute('auth/user/password'),
  PASSWORD_FORGOT: makeApiRoute('auth/forgot-password'),
  PASSWORD_RESET: makeApiRoute('auth/reset-password'),

  // ── Tenants + memberships ──────────────────────────────────────
  TENANTS: makeApiRoute('tenants'),
  TENANT_MEMBERSHIPS: makeApiRoute('tenant-memberships'),

  // ── On-prem appliances (super_admin): catalog, tokens, command queue ──
  // `appliances/{id}/tokens[/{token}]`, `appliances/{id}/commands` via path args.
  APPLIANCES: makeApiRoute('appliances'),

  // ── MDM profiles a flash station can set a unit up for (super_admin) ──
  ASSEMBLY_MDM_PROFILES: makeApiRoute('assembly-mdm-profiles'),

  // ── Users (role mgmt folded into the main payload) ────────────
  USERS: makeApiRoute('users'),
  // Bulk explicit (direct) access over a selection or a whole filter set.
  USERS_BULK_ACCESS: makeApiRoute('users/bulk-access'),

  // ── Groups + memberships ───────────────────────────────────────
  GROUPS: makeApiRoute('groups'),
  USER_GROUPS: makeApiRoute('user-groups'),
  // Group → members sub-resource. Paginated reads + bulk
  // add/remove with immediate writes; scales to thousands of
  // members per group (large-relation pattern).
  GROUP_MEMBERS: makeApiRoute('groups/{?}/members'),
  GROUP_MEMBERS_REMOVE: makeApiRoute('groups/{?}/members/remove'),
  GROUP_MEMBERS_ACCESS_VIA_GROUPS: makeApiRoute('groups/{?}/members/access-via-groups'),
  // Department-wide department-access mutation. Queues a chunked job and
  // answers with the member count; the operator gets a site notification
  // when the run finishes.
  GROUP_ACCESS_APPLY: makeApiRoute('groups/{?}/access/apply'),
  GROUP_ACCESS_REVOKE: makeApiRoute('groups/{?}/access/revoke'),

  // ── Guest registration workflow ────────────────────────────────
  // Settings (auto_approve + default_group_id) on a per-tenant
  // TenantConfig row. Approve/deny are POST actions on a User; the
  // list itself is served by USERS with `?guest_status=pending`.
  GUESTS_SETTINGS: makeApiRoute('guests/settings'),
  GUESTS_STATS: makeApiRoute('guests/stats'),
  GUESTS_APPROVE: makeApiRoute('guests/{?}/approve'),
  GUESTS_DENY: makeApiRoute('guests/{?}/deny'),

  // ── Tracker — unified historical trace ───────────────────────
  // Read-only polymorphic timeline (events + entity-emergence
  // nodes). Axis-filter shaped like Access Matrix. Cursor
  // pagination via `cursor` param + `next_cursor` in response.
  TRACKER_TRACE: makeApiRoute('tracker/trace'),
  // Streaming XLSX export of the same filtered trace — consumed by
  // the Reports page. Use `getFullUrl(...)` + query params to build
  // a direct download URL (browser nav, not axios) so the streamed
  // bytes flow straight into the operator's downloads folder.
  TRACKER_REPORT_EXPORT: makeApiRoute('tracker/report/export'),
  // Anchor-picker entity search — tracker-only, returns soft-deleted
  // entities too (gated to TENANT_TRACKER_VIEW server-side). Resolves by
  // `search` (typeahead) or `id` (seed a pre-set anchor). NEVER reuse the
  // shared resource endpoints for this — they keep deleted rows hidden.
  TRACKER_ANCHORS: makeApiRoute('tracker/anchors'),
  // Activity-log report aggregation — KPI strip + 24h "by Kind" series.
  // Counts the same event stream as TRACKER_TRACE (server-side). Honors
  // the trace anchor + a coarse location/department scope.
  TRACKER_ACTIVITY_STATS: makeApiRoute('tracker/activity-stats'),

  // ── Dashboard layout (per-user widget grid placement) ─────────
  // GET/PUT/DELETE on a single resource (no id segment — keyed off
  // Auth::id() server-side). Resolution chain: user → tenant default
  // → starter pack (see DashboardLayoutController kdoc).
  DASHBOARD_LAYOUT: makeApiRoute('dashboard-layout'),

  // ── Device catalog ─────────────────────────────────────────────
  DEVICE_MODELS: makeApiRoute('device-models'),
  DEVICES: makeApiRoute('devices'),
  DEVICE_BAY_ASSIGNMENTS: makeApiRoute('device-bay-assignments'),
  // Per-tenant customizable deletion-reason list. GET feeds the
  // inventory delete dialog; PATCH edits the list (Configs section).
  DEVICE_DELETION_REASONS: makeApiRoute('device-deletion-reasons'),
  // Tenant-wide general settings — timezone (Settings → General).
  GENERAL_SETTINGS: makeApiRoute('general-settings'),
  // Per-tenant late-device reminder policy + message (Settings → Notifications).
  DEVICE_LATE_NOTIFICATION: makeApiRoute('device-late-notification'),
  // Per-tenant scheduled summary report policy (Settings → Scheduled reports).
  SCHEDULED_REPORTS: makeApiRoute('scheduled-reports'),
  // Device Status Report — paginated rows + status aggregates (charts).
  DEVICE_STATUS_REPORT: makeApiRoute('devices/status-report'),
  DEVICE_STATUS_REPORT_SUMMARY: makeApiRoute('devices/status-report/summary'),
  // Asset Accounting — one read per pane (`.get('overview' | 'loss' |
  // 'inventory-value' | 'repair' | 'rules' | 'settings')`) + the records the
  // panes act on. Settings is `PATCH asset-accounting/settings`.
  ASSET_ACCOUNTING: makeApiRoute('asset-accounting'),
  REPAIR_TICKETS: makeApiRoute('repair-tickets'),
  REPAIR_VENDORS: makeApiRoute('repair-vendors'),
  REPAIR_COMPONENTS: makeApiRoute('repair-components'),
  ASSET_RULES: makeApiRoute('asset-rules'),

  // ── Bulk import/export (tenant_admin) ──────────────────────────
  // Export = streamed .xlsx via `route.getFullUrl()` browser-nav (same
  // as TRACKER_REPORT_EXPORT). Import = POST a filled template; the
  // server returns the per-row result + failure list. See app/Exports +
  // app/Imports on the BE.
  DEVICES_EXPORT_TEMPLATE: makeApiRoute('devices/export-template'),
  DEVICES_EXPORT: makeApiRoute('devices/export'),
  DEVICES_IMPORT: makeApiRoute('devices/import'),
  // Bulk soft-delete (mass-select). POST devices/bulk-delete with
  // `{ ids: number[] }` → `{ deleted: number }`. Separate makeApiRoute
  // entry per the custom-sub-path convention (mirrors DEVICES_EXPORT).
  DEVICES_BULK_DELETE: makeApiRoute('devices/bulk-delete'),
  USERS_EXPORT_TEMPLATE: makeApiRoute('users/export-template'),
  USERS_EXPORT: makeApiRoute('users/export'),
  USERS_IMPORT: makeApiRoute('users/import'),
  GROUPS_EXPORT_TEMPLATE: makeApiRoute('groups/export-template'),
  GROUPS_EXPORT: makeApiRoute('groups/export'),
  GROUPS_IMPORT: makeApiRoute('groups/import'),

  // ── Kiosks + bays + commands + locations + templates ──────────
  KIOSKS: makeApiRoute('kiosks'),
  KIOSK_COMMANDS: makeApiRoute('kiosks/{?}/commands'),
  // Retired-locker lifecycle: restore un-deletes (bays come back with it),
  // purge is PERMANENT and platform-only. See KioskController::restore/purge
  // for what a restore can and cannot recover.
  KIOSK_RESTORE: makeApiRoute('kiosks/{?}/restore'),
  KIOSK_PURGE: makeApiRoute('kiosks/{?}/purge'),
  KIOSK_BAYS: makeApiRoute('kiosk-bays'),
  KIOSK_BAY_STATES: makeApiRoute('kiosk-bay-states'),
  KIOSK_TEMPLATES: makeApiRoute('kiosk-templates'),
  LOCATIONS: makeApiRoute('locations'),
  // Abstract tag/folder hierarchy (org tree). Sub-paths used inline:
  // `.post(id,'attach')`, `.post('detach')`. Server-side only.
  TAG_NODES: makeApiRoute('tag-nodes'),

  // ── OTA app-release catalog + fleet dispatch ──────────────────
  OTA_RELEASES: makeApiRoute('ota/releases'),
  OTA_PROGRESS: makeApiRoute('ota/progress'),
  OTA_REFRESH: makeApiRoute('ota/refresh'),
  OTA_DISPATCH: makeApiRoute('ota/dispatch'),
  OTA_CANCEL: makeApiRoute('ota/cancel'),
  OTA_APK_DOWNLOAD_OFFER: makeApiRoute('ota/apk-download-offer'),
  OTA_APK_DOWNLOAD: makeApiRoute('ota/releases/{?}/download'),

  // ── Access grants ──────────────────────────────────────────────
  // User-side grant sync (bay + location lists) is folded into the
  // main `POST/PATCH /users[/{id}]` payload via `membership.bay_grants`
  // / `membership.location_grants`. Standalone bulk endpoints removed.
  // Per-row CRUD stays available for direct API consumers + the
  // editor's loaders (index/GET only on the editor side).
  USER_BAY_ASSIGNMENTS: makeApiRoute('user-bay-assignments'),
  USER_LOCATION_ASSIGNMENTS: makeApiRoute('user-location-assignments'),
  GROUP_BAY_ASSIGNMENTS: makeApiRoute('group-bay-assignments'),
  GROUP_BAY_ASSIGNMENTS_BULK: makeApiRoute('group-bay-assignments/bulk'),

  // ── Master keys (8-digit standing passcodes) ───────────────────
  MASTER_KEYS: makeApiRoute('master-keys'),
  MASTER_KEY_REVOKE: makeApiRoute('master-keys/{?}/revoke'),

  // ── Access Matrix projection (axis-filter shaped) ──────────────
  ACCESS_MATRIX: makeApiRoute('access-matrix'),

  // ── Branding (multi-asset tenant brand surface) ────────────────
  BRANDING: makeApiRoute('branding'),
  BRANDING_SETTINGS: makeApiRoute('branding/settings'),
  BRANDING_ASSET: makeApiRoute('branding/asset/{?}'),

  // ── Integrations (third-party directory sync) ───────────────────
  // GET  /api/integrations          → index (connections+available+catalog)
  // POST /api/integrations/{key}    → add connection (key as path segment)
  INTEGRATIONS: makeApiRoute('integrations'),
  // PATCH /api/integrations/{key}   → update; DELETE → remove. {?} = key.
  INTEGRATIONS_PROVIDER: makeApiRoute('integrations/{?}'),
  // POST /api/integrations/{key}/test  — {?} = provider key.
  INTEGRATIONS_TEST: makeApiRoute('integrations/{?}/test'),
  // POST /api/integrations/{key}/sync  — {?} = provider key.
  // Group-scoped sync: the live option list for the picker, and the dry-run
  // that reports how many members a scope change would depart.
  INTEGRATION_DIRECTORY_GROUPS: makeApiRoute('integrations/{?}/directory-groups'),
  INTEGRATION_SCOPE_PREVIEW: makeApiRoute('integrations/{?}/scope-preview'),
  // Column matching: the directory's populated member attributes, sampled live.
  INTEGRATION_DIRECTORY_ATTRIBUTES: makeApiRoute('integrations/{?}/directory-attributes'),
  // The directory link behind one membership - {?} = membership id.
  INTEGRATION_MEMBERSHIP_LINK: makeApiRoute('integrations/membership-link/{?}'),
  INTEGRATIONS_SYNC: makeApiRoute('integrations/{?}/sync'),

  // ── Orders + frozen children + transaction log ─────────────────
  ORDERS: makeApiRoute('orders'),
  ORDER_ITEMS: makeApiRoute('order-items'),
  ORDER_ACCESS_INFO: makeApiRoute('order-access-info'),
  ORDER_TRANSACTIONS: makeApiRoute('order-transactions'),

  // ── Append-only audit ──────────────────────────────────────────
  BAY_STATE_CHANGES: makeApiRoute('bay-state-changes'),
  ACTIVITY_LOGS: makeApiRoute('activity-logs'),

  // ── Notifications inbox (per-user, custom site channel) ─────────
  NOTIFICATIONS: makeApiRoute('notifications'),
  NOTIFICATIONS_UNREAD_COUNT: makeApiRoute('notifications/unread-count'),
  NOTIFICATIONS_READ_ALL: makeApiRoute('notifications/read-all'),
  NOTIFICATIONS_READ: makeApiRoute('notifications/{?}/read'),
  NOTIFICATIONS_UNREAD: makeApiRoute('notifications/{?}/unread'),
  // Bulk mark by id (inbox selection bar) — POST { ids: number[] }.
  NOTIFICATIONS_BULK_READ: makeApiRoute('notifications/bulk/read'),
  NOTIFICATIONS_BULK_UNREAD: makeApiRoute('notifications/bulk/unread'),

  // ── Sync log (paginated log + entry detail) and the sync pane's feed ─
  SYNC_ACTIVITIES: makeApiRoute('sync-activities'),
  SYNC_ACTIVITIES_RECENT: makeApiRoute('sync-activities/recent'),

  // ── Health probe (public; no auth) ─────────────────────────────
  HEALTH: makeApiRoute('health'),
}

export const useApiRoutes = (): APIRoutes => ApiRoutes
